import React from 'react'
import { Region, XP_REQUEST_MODE, type ComponentProps, type LayoutData } from '@enonic/react-components'
import SubpageHeroPrototype from '/react4xp/components/prototype/SubpageHero'
import CardPrototype from '/react4xp/components/prototype/Card'
import PaginationPrototype from '/react4xp/components/prototype/Pagination'
import type { CardProps } from '/react4xp/components/prototype/Card/types'
import type { SubpageHeroFilter } from '/react4xp/components/prototype/SubpageHero/types'

/**
 * Article List layout wrapping the styled prototype components.
 *
 * Filter and pagination callbacks update the query string so the server reruns
 * the processor; edit mode keeps those controls inert.
 *
 * This is a Layout (user decision, 2026-07-31), so the `main` Region renders
 * unconditionally - including when zero articles match. Gating it behind
 * `cards.length` would leave editors unable to drop anything into the region.
 */
type FilterOption = {
	value: string
	label: string
}

type ArticleCard = {
	href: string | null
	isExternal: boolean
	name: string
	intro?: string
	image?: { src: string; alt?: string }
	type: string
	service: string[]
}

type Pagination = {
	currentPage: number
	totalPages: number
	pageSize: number
	totalItems: number
	hasPrevious: boolean
	hasNext: boolean
}

type ArticleListStrings = {
	typeFilter: string
	type: string
	serviceFilter: string
	service: string
	readMore: string
	filterAll: string
	emptyMessage: string
	cardLabel: string
}

type ArticleListLayoutData = {
	overline?: string
	heading?: string
	intro?: string
	items?: ArticleCard[]
	filters?: { types: FilterOption[]; services: FilterOption[] }
	selectedFilters?: { type: string | null; service: string | null }
	strings?: ArticleListStrings
	pagination?: Pagination
	error?: string
}

// Filter values are human-readable and can equal "all", unlike ReferenceList's content ids.
const ALL_VALUE = '__all__'

// Card is an anchor, so omit articles without an href; its badge is a static translation.
function toCard(item: ArticleCard, cardLabel?: string): Omit<CardProps, 'titleSize' | 'variant'> | null {
	if (!item.href) return null

	return {
		image: item.image && { src: item.image.src, alt: item.image.alt || item.name },
		label: cardLabel,
		title: item.name,
		url: item.href,
		isExternal: item.isExternal,
	}
}

export const ArticleList = ({ component, data, meta }: ComponentProps<LayoutData>) => {
	const {
		overline,
		heading,
		items = [],
		filters,
		selectedFilters,
		strings,
		pagination,
	} = (data || {}) as ArticleListLayoutData

	// Navigation must remain inert inside Content Studio's editor frame.
	const isEditMode = meta?.mode === XP_REQUEST_MODE.EDIT

	function navigate(params: Record<string, string | null>) {
		if (isEditMode || typeof window === 'undefined') return

		const url = new URL(window.location.href)
		Object.keys(params).forEach((key) => {
			const value = params[key]
			if (value) {
				url.searchParams.set(key, value)
			} else {
				url.searchParams.delete(key)
			}
		})
		window.location.assign(url.toString())
	}

	// Reset paging after filtering so a smaller result set cannot open past its end.
	function buildFilter(id: string, label: string, options: FilterOption[], selected: string | null): SubpageHeroFilter {
		return {
			id,
			label,
			options: [{ value: ALL_VALUE, label: strings?.filterAll || '' }, ...options],
			value: selected || ALL_VALUE,
			onChange: (value: string) => navigate({ [id]: value === ALL_VALUE ? null : value, page: null }),
		}
	}

	// Hide filters with no options across the full result set.
	const heroFilters: SubpageHeroFilter[] = [
		{ id: 'type', label: strings?.typeFilter || '', options: filters?.types || [], selected: selectedFilters?.type || null },
		{ id: 'service', label: strings?.serviceFilter || '', options: filters?.services || [], selected: selectedFilters?.service || null },
	]
		.filter((filter) => filter.options.length > 0)
		.map((filter) => buildFilter(filter.id, filter.label, filter.options, filter.selected))

	const cards = items.map((item) => toCard(item, strings?.cardLabel)).filter((card): card is Omit<CardProps, 'titleSize' | 'variant'> => card !== null)
	const currentPage = pagination?.currentPage || 1
	const totalPages = Math.max(pagination?.totalPages || 0, 1)

	return (
		<>
			<SubpageHeroPrototype overline={overline} heading={heading || ''} filters={heroFilters} />

			{/* Mirrors prototype/src/components/ArticleList/index.tsx; I99X-384 hides single-page pagination. */}
			<section className="my-12">
				<div className="container flex flex-col gap-8">
					{cards.length > 0 ? (
						<>
							<div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-8">
								{cards.map((card, i) => (
									<CardPrototype key={i} {...card} titleSize="h2" variant="article" />
								))}
							</div>
							{totalPages > 1 && (
								<PaginationPrototype
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={(page: number) => navigate({ page: page > 1 ? String(page) : null })}
								/>
							)}
						</>
					) : (
						<p className="body text-black-950 text-center">{strings?.emptyMessage}</p>
					)}
				</div>
			</section>
			<Region name="main" data={component?.regions?.main?.components || []} meta={meta} />
		</>
	)
}
